import React, { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Link } from 'react-router'
import { connectParams } from 'lp-hoc'
import { Spinner, ButtonArea } from 'lp-components'
import { reset } from 'redux-form'
import { scroller } from 'react-scroll'
import { Modal } from 'components'
import {
  ShareIcon,
  TrashIcon,
  EditIcon,
  LockIcon,
  UnlockIcon,
  FlagIcon,
} from 'components/icons/basic'
import NewLearningHubPost from './new-learning-hub-post'
import {
  FlagModal,
  ViewPostCard,
  CommentsList,
  ProfileDetails,
  ConfirmUnflagModal,
} from 'educator-portal-components'
import { selectors as lhSelectors } from 'learning-hub-reducer'
import { selectors as educatorSelectors } from 'educator-portal-reducer'
import * as apiActions from 'api-actions'
import {
  clipboard,
  useOnOutsideClick,
  getUserDisplayName,
  displaySubmitFailure,
  getPostRootPath,
} from 'utils'
import * as flashActions from 'redux-flash'
import * as routerActions from 'react-router-redux'
import { first, isEmpty, get, find, last } from 'lodash'
import classnames from 'classnames'

const propTypes = {
  postId: PropTypes.number.isRequired,
  fetchPost: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  post: Types.post,
  flashErrorMessage: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  archivePost: PropTypes.func.isRequired,
  lockPost: PropTypes.func.isRequired,
  reopenPost: PropTypes.func.isRequired,
  archiveComment: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
  currentUserId: PropTypes.number.isRequired,
  userDetails: Types.user.isRequired,
  isModerator: PropTypes.bool.isRequired,
  createComment: PropTypes.func.isRequired,
  updateComment: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  addBookmark: PropTypes.func.isRequired,
  removeBookmark: PropTypes.func.isRequired,
  addLike: PropTypes.func.isRequired,
  destroyLike: PropTypes.func.isRequired,
  bookmarks: PropTypes.arrayOf(Types.bookmark).isRequired,
  fetchUser: PropTypes.func.isRequired,
  selectedUser: Types.directoryUser,
  createFlag: PropTypes.func.isRequired,
  createLearningHubForumFlag: PropTypes.func.isRequired,
  unflag: PropTypes.func.isRequired,
  disagreeWithFlag: PropTypes.func.isRequired,
  userMentionSuggestions: PropTypes.arrayOf(Types.mentionSuggestion),
  fetchMentionSuggestions: PropTypes.func.isRequired,
  fetchNotifications: PropTypes.func.isRequired,
}

const defaultProps = {
  post: null,
  selectedUser: null,
}

function LearningHubDiscussion({
  postId,
  fetchPost,
  fetchComments,
  post,
  flashSuccessMessage,
  flashErrorMessage,
  location,
  push,
  archivePost,
  lockPost,
  reopenPost,
  archiveComment,
  redirect,
  currentUserId,
  userDetails,
  isModerator,
  createComment,
  updateComment,
  resetForm,
  addBookmark,
  removeBookmark,
  addLike,
  destroyLike,
  bookmarks,
  fetchUser,
  selectedUser,
  createFlag,
  createLearningHubForumFlag,
  unflag,
  disagreeWithFlag,
  userMentionSuggestions,
  fetchMentionSuggestions,
  fetchNotifications,
}) {
  const { profile: userProfile, email: userEmail } = userDetails
  const fetchPostAndComments = useCallback(() => {
    fetchPost(postId)
      .then(() => {
        fetchComments(postId)
      })
      .catch(() => redirect('/not-found'))
  }, [postId, fetchPost, fetchComments, redirect])

  useEffect(() => {
    fetchPostAndComments()
    fetchMentionSuggestions()
  }, [fetchPostAndComments, fetchMentionSuggestions])

  useEffect(() => {
    if (post && post.id === postId) {
      setIsLocked(post.state === Types.POST_STATES.LOCKED)
    }
  }, [post, postId])

  const rootPath = getPostRootPath(location.pathname)
  const postPath = `${rootPath}discussions/${postId}`
  const postUrl = window.origin + postPath
  const [focusedContainerId, setFocusedContainerId] = useState(null)
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)
  const [isPostFormOpen, setIsPostFormOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [isProfileDetailsOpen, setIsProfileDetailsOpen] = useState(false)
  const focusedRef = useOnOutsideClick(() => push(postPath))
  const [isLocked, setIsLocked] = useState(false)
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false)
  const [idToUnflag, setIdToUnflag] = useState(null)
  const [reviewableId, setReviewableId] = useState(null)
  const [reviewableType, setReviewableType] = useState(null)

  const copyPostUrl = useCallback(() => {
    clipboard
      .writeText(postUrl)
      .then(() => {
        flashSuccessMessage('The discussion link was copied to your clipboard.')
      })
      .catch((err) => {
        const message =
          err.message ||
          'Oops, unable to copy the discussion link to your clipboard.'
        flashErrorMessage(message)
      })
  }, [postUrl, flashSuccessMessage, flashErrorMessage])

  const copyCommentUrl = useCallback(
    (commentContainerId) => {
      clipboard
        .writeText(`${postUrl}#${commentContainerId}`)
        .then(() => {
          flashSuccessMessage('The comment link was copied to your clipboard.')
        })
        .catch((err) => {
          const message =
            err.message ||
            'Oops, unable to copy the comment link to your clipboard.'
          flashErrorMessage(message)
        })
    },
    [postUrl, flashSuccessMessage, flashErrorMessage]
  )

  const requestArchivePost = useCallback(() => {
    archivePost(postId)
      .then(() => {
        flashSuccessMessage('Your discussion was successfully removed.')
        redirect(rootPath)
      })
      .catch((err) => {
        setIsArchiveConfirmOpen(false)
        const message = err.message || 'Oops, unable to remove your discussion.'
        flashErrorMessage(message)
      })
  }, [
    postId,
    archivePost,
    flashSuccessMessage,
    flashErrorMessage,
    redirect,
    rootPath,
  ])

  const requestLockPost = useCallback(() => {
    lockPost(postId)
      .then(() => {
        flashSuccessMessage(
          'Commenting has been turned off for this discussion.'
        )
        setIsLocked(true)
      })
      .catch((err) => {
        const message =
          err.message ||
          'Oops, unable to turn off commenting for this discussion'
        flashErrorMessage(message)
      })
  }, [postId, lockPost, flashSuccessMessage, flashErrorMessage])

  const requestReopenPost = useCallback(() => {
    reopenPost(postId)
      .then(() => {
        flashSuccessMessage(
          'Commenting has been turned on for this discussion.'
        )
        setIsLocked(false)
      })
      .catch((err) => {
        const message =
          err.message ||
          'Oops, unable to turn on commenting for this discussion'
        flashErrorMessage(message)
      })
  }, [postId, reopenPost, flashSuccessMessage, flashErrorMessage])

  const getBookmarkId = useCallback(
    (bookmarkableId, bookmarkableType) => {
      const matchingBookmark = find(bookmarks, {
        bookmarkableId,
        bookmarkableType,
      })
      return get(matchingBookmark, 'id')
    },
    [bookmarks]
  )

  const discussionBookmarkType = Types.BOOKMARKABLE.LEARNING_HUB_DISCUSSION

  const postBookmarkId = getBookmarkId(postId, discussionBookmarkType)
  const selectedUserBookmarkId = getBookmarkId(
    selectedUserId,
    Types.BOOKMARKABLE.USER
  )
  const isPostBookmarked = !!postBookmarkId
  const isSelectedUserBookmarked = !!selectedUserBookmarkId

  const requestAddBookmark = useCallback(
    (type) => {
      const bookmarkableId =
        type === Types.BOOKMARKABLE.USER ? selectedUserId : postId
      addBookmark({ bookmarkableId, bookmarkableType: type })
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [
      postId,
      selectedUserId,
      addBookmark,
      flashSuccessMessage,
      flashErrorMessage,
    ]
  )
  const requestRemoveBookmark = useCallback(
    (type) => {
      const bookmarkId =
        type === Types.BOOKMARKABLE.USER
          ? selectedUserBookmarkId
          : postBookmarkId
      removeBookmark({ bookmarkId, bookmarkableType: type })
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [
      postBookmarkId,
      selectedUserBookmarkId,
      removeBookmark,
      flashSuccessMessage,
      flashErrorMessage,
    ]
  )

  const requestAddLike = useCallback(
    (likeableId, likeableType) => {
      addLike({
        likeableId,
        likeableType,
      }).catch(({ errorMessage }) => {
        flashErrorMessage(errorMessage)
      })
    },
    [addLike, flashErrorMessage]
  )

  const requestDestroyLike = useCallback(
    (id) => {
      destroyLike({ id }).catch(({ errorMessage }) => {
        flashErrorMessage(errorMessage)
      })
    },
    [destroyLike, flashErrorMessage]
  )

  const requestArchiveComment = useCallback(
    (commentId) => {
      archiveComment(commentId)
        .then(() => {
          flashSuccessMessage('Your comment was successfully removed.')
        })
        .catch((err) => {
          const message = err.message || 'Oops, unable to remove your comment.'
          flashErrorMessage(message)
        })
    },
    [archiveComment, flashSuccessMessage, flashErrorMessage]
  )

  const requestUpdateComment = useCallback(
    (commentId, content, attachments) => {
      updateComment(commentId, content, attachments)
        .then(() => {
          flashSuccessMessage('Your comment was successfully edited!')
        })
        .catch((err) => {
          const message = err.message || 'Oops, unable to edit your comment.'
          flashErrorMessage(message)
        })
    },
    [updateComment, flashSuccessMessage, flashErrorMessage]
  )

  const requestCreateEducatorFlag = useCallback(
    ({ reviewableId, reviewableType, reason }) => {
      createFlag({ reviewableId, reviewableType, reason })
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [createFlag, flashSuccessMessage, flashErrorMessage]
  )

  const requestCreateLearningHubForumFlag = useCallback(
    ({ reviewableId, reviewableType, reason }) => {
      createLearningHubForumFlag({
        flaggableType: reviewableType,
        flaggableId: reviewableId,
        comment: reason,
      })
        .then(() => {
          flashSuccessMessage(
            'Thank you. A site admin will review this content.'
          )
          return fetchPostAndComments()
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [
      createLearningHubForumFlag,
      flashSuccessMessage,
      flashErrorMessage,
      fetchPostAndComments,
    ]
  )

  const requestUnflag = useCallback(
    (flagId) => {
      unflag(flagId)
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [unflag, flashSuccessMessage, flashErrorMessage]
  )

  const requestDisagreeWithFlag = useCallback(
    (flagId) => {
      disagreeWithFlag(flagId)
        .then(({ successMessage }) => {
          flashSuccessMessage(successMessage)
        })
        .catch(({ errorMessage }) => {
          flashErrorMessage(errorMessage)
        })
    },
    [disagreeWithFlag, flashSuccessMessage, flashErrorMessage]
  )

  useEffect(() => {
    const elementId = location.hash.slice(1)
    setFocusedContainerId(elementId)
  }, [location.hash])

  useEffect(() => {
    if (!focusedContainerId) return
    if (focusedContainerId.includes(Types.MENTIONED_USER_ID_PREFIX)) {
      const userId = last(focusedContainerId.split('-'))
      setSelectedUserId(parseInt(userId))
      setIsProfileDetailsOpen(true)
    }
  }, [focusedContainerId])

  useEffect(() => {
    if (selectedUserId) fetchUser(selectedUserId)
  }, [selectedUserId, fetchUser])
  const hasSelectedUserLoaded =
    selectedUser && selectedUser.id === selectedUserId

  const hasCorrectPost = post && post.id === postId
  const hasCorrectComments = useMemo(() => {
    if (!post || !post.comments) return false
    if (isEmpty(post.comments)) return true
    return first(post.comments).postId === postId
  }, [post, postId])
  const isOriginalPoster = hasCorrectPost && post.user.id === currentUserId

  useEffect(() => {
    if (hasCorrectComments && focusedContainerId && focusedRef.current) {
      scroller.scrollTo(focusedContainerId, { smooth: true })
      focusedRef.current.focus()
    }
  }, [hasCorrectComments, focusedContainerId, focusedRef])

  useEffect(() => {
    if (isProfileDetailsOpen && isFlagModalOpen) setIsProfileDetailsOpen(false)
  }, [isProfileDetailsOpen, isFlagModalOpen])

  if (!hasCorrectPost) return <Spinner />

  const isCoPMember = true

  const setReviewableAndOpenModal = (reviewableId, reviewableType) => {
    setReviewableId(reviewableId)
    setReviewableType(reviewableType)
    setIsFlagModalOpen(true)
  }

  const hasForumFlagsOnPost = !isEmpty(post.forumFlags || post.forum_flags)

  const requestRemoveEducatorFlag = (flagId) => {
    if (!isModerator) return requestUnflag(flagId)

    return requestDisagreeWithFlag(flagId)
  }

  const onSelectFlagAction = (flags, entityId, reviewableType) => {
    if (reviewableType === Types.REVIEWABLE.PROFILE) {
      if (isEmpty(flags))
        return setReviewableAndOpenModal(entityId, reviewableType)
      if (!isModerator) return setIdToUnflag(first(flags).id)

      requestRemoveEducatorFlag(first(flags).id)
      return
    }

    if (
      reviewableType === Types.LEARNING_HUB_FLAGGABLE.DISCUSSION ||
      reviewableType === Types.LEARNING_HUB_FLAGGABLE.COMMENT
    ) {
      const list = flags || []
      if (list.some((f) => (f.reporterId ?? f.reporter_id) === currentUserId)) {
        flashErrorMessage('You have already flagged this for review.')
        return
      }
      return setReviewableAndOpenModal(entityId, reviewableType)
    }
  }

  const sharePostAction = {
    icon: ShareIcon,
    label: 'Share Discussion',
    onSelect: copyPostUrl,
  }

  const forumFlagsOnPost = post.forumFlags || post.forum_flags || []
  const myForumFlagOnPost = forumFlagsOnPost.some(
    (f) => (f.reporterId ?? f.reporter_id) === currentUserId
  )

  const flagPostAction = {
    icon: FlagIcon,
    label: myForumFlagOnPost
      ? 'You flagged for review'
      : hasForumFlagsOnPost
      ? 'Flagged for review'
      : 'Flag for review',
    onSelect: () =>
      onSelectFlagAction(
        forumFlagsOnPost,
        postId,
        Types.LEARNING_HUB_FLAGGABLE.DISCUSSION
      ),
  }

  const publicActions = [sharePostAction, flagPostAction]

  const lockPostAction = {
    icon: LockIcon,
    label: 'Lock Commenting',
    onSelect: requestLockPost,
  }

  const unlockPostAction = {
    icon: UnlockIcon,
    label: 'Unlock Commenting',
    onSelect: requestReopenPost,
  }

  const moderatorActions = isLocked ? [unlockPostAction] : [lockPostAction]

  const editPostAction = {
    icon: EditIcon,
    label: 'Edit Discussion',
    onSelect: () => setIsPostFormOpen(true),
  }

  const archivePostAction = {
    icon: TrashIcon,
    label: 'Remove Discussion',
    onSelect: () => setIsArchiveConfirmOpen(true),
  }

  const posterActions = [editPostAction, archivePostAction]

  const getPostActions = () => {
    const actions = [...publicActions]

    if (isModerator) {
      actions.push(...moderatorActions)
    }

    if (isOriginalPoster) {
      actions.push(...posterActions)
    }

    return actions
  }

  return (
    <div className="post">
      <Link className="link-secondary no-underline" to={rootPath}>
        ← <span className="underline">Return to All Discussions</span>
      </Link>
      <ViewPostCard
        post={post}
        postPath={postPath}
        actions={getPostActions()}
        showProfileDetails={() => {
          setSelectedUserId(post.user.id)
          setIsProfileDetailsOpen(true)
        }}
        isBookmarked={isPostBookmarked}
        toggleBookmark={() => {
          if (isPostBookmarked)
            return requestRemoveBookmark(discussionBookmarkType)
          return requestAddBookmark(discussionBookmarkType)
        }}
        hasFlags={hasForumFlagsOnPost}
        flaggedBannerLabel="Flagged for review"
        addLike={requestAddLike}
        destroyLike={requestDestroyLike}
        userDetails={userDetails}
        postLikeableType={Types.LIKEABLE.LEARNING_HUB_DISCUSSION}
      />
      {!hasCorrectComments ? (
        <Spinner />
      ) : (
        <React.Fragment>
          <CommentsList
            comments={post.comments}
            copyCommentUrl={copyCommentUrl}
            highlightedContainerId={focusedContainerId}
            highlightedRef={focusedRef}
            currentUserId={currentUserId}
            requestArchiveComment={requestArchiveComment}
            requestUpdateComment={requestUpdateComment}
            showProfileDetails={(userId) => {
              setSelectedUserId(userId)
              setIsProfileDetailsOpen(true)
            }}
            showUnfoundCommentError={() => {
              flashErrorMessage("Sorry, we can't find that comment.")
            }}
            createComment={createComment}
            resetForm={resetForm}
            isCommentingDisabled={isLocked || (!isModerator && !isCoPMember)}
            currentUserDisplayName={getUserDisplayName(userProfile, userEmail)}
            currentUserProfileImgUrl={get(userProfile, 'avatarUrl')}
            commentableId={postId}
            commentableType={Types.COMMENTABLE.LEARNING_HUB_DISCUSSION}
            onSelectFlagAction={onSelectFlagAction}
            isModerator={isModerator}
            flashSuccessMessage={flashSuccessMessage}
            flashErrorMessage={flashErrorMessage}
            mentionSuggestions={userMentionSuggestions}
            addLike={requestAddLike}
            destroyLike={requestDestroyLike}
            commentLikeableType={Types.LIKEABLE.LEARNING_HUB_COMMENT}
            learningHubForum
            flaggedBannerLabel="Flagged for review"
            onCommentSubmitSuccess={fetchNotifications}
          />
          {isLocked ? (
            <p className="text-center">
              <em>Commenting has been turned off for this discussion.</em>
            </p>
          ) : null}
        </React.Fragment>
      )}
      {isArchiveConfirmOpen && (
        <Modal onClose={() => setIsArchiveConfirmOpen(false)}>
          <h2>Are you sure?</h2>
          <div className="modal-content">
            <p>
              By removing your discussion, you will also be removing any
              comments on your discussion.
            </p>
            <ButtonArea>
              <button
                type="button"
                className="button-warn"
                onClick={requestArchivePost}
              >
                Remove
              </button>
              <button
                type="button"
                className="button-grey-light"
                onClick={() => setIsArchiveConfirmOpen(false)}
              >
                Cancel
              </button>
            </ButtonArea>
          </div>
        </Modal>
      )}
      {isPostFormOpen && (
        <Modal onClose={() => setIsPostFormOpen(false)}>
          <NewLearningHubPost
            postToEdit={post}
            onSubmitSuccess={() => {
              fetchPostAndComments()
              setIsPostFormOpen(false)
              flashSuccessMessage('Your discussion was edited successfully!')
            }}
            onCancel={() => setIsPostFormOpen(false)}
          />
        </Modal>
      )}
      {isProfileDetailsOpen && hasSelectedUserLoaded && (
        <Modal
          onClose={() => setIsProfileDetailsOpen(false)}
          className={classnames('show-flash', {
            'is-flagged': !isEmpty(selectedUser.flags),
          })}
        >
          <div className="discussion-card">
            <ProfileDetails
              user={selectedUser}
              isOwnProfile={selectedUserId === currentUserId}
              isBookmarked={isSelectedUserBookmarked}
              toggleBookmark={() => {
                if (isSelectedUserBookmarked)
                  return requestRemoveBookmark(Types.BOOKMARKABLE.USER)
                return requestAddBookmark(Types.BOOKMARKABLE.USER)
              }}
              onSelectFlagAction={onSelectFlagAction}
              isModerator={isModerator}
            />
          </div>
        </Modal>
      )}
      {isFlagModalOpen && (
        <FlagModal
          {...(reviewableType === Types.REVIEWABLE.PROFILE
            ? {}
            : {
                title: 'Flag for review',
                reasonLabel: 'Please describe what we should review.',
                submitLabel: 'Submit',
              })}
          onSubmit={({ reason }) => {
            if (reviewableType === Types.REVIEWABLE.PROFILE) {
              return requestCreateEducatorFlag({
                reviewableId,
                reviewableType,
                reason,
              })
            }
            return requestCreateLearningHubForumFlag({
              reviewableId,
              reviewableType,
              reason,
            })
          }}
          onSubmitSuccess={() => setIsFlagModalOpen(false)}
          onClose={() => setIsFlagModalOpen(false)}
          onSubmitFail={displaySubmitFailure}
        />
      )}
      {idToUnflag && (
        <ConfirmUnflagModal
          onConfirm={() => {
            requestRemoveEducatorFlag(idToUnflag)
            setIdToUnflag(null)
          }}
          onClose={() => setIdToUnflag(null)}
        />
      )}
    </div>
  )
}

LearningHubDiscussion.propTypes = propTypes
LearningHubDiscussion.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    post: lhSelectors.post(state),
    currentUserId: lhSelectors.userId(state),
    userDetails: lhSelectors.userDetails(state),
    isModerator: lhSelectors.isModerator(state),
    bookmarks: lhSelectors.bookmarks(state),
    selectedUser: educatorSelectors.directoryUser(state),
    userMentionSuggestions: lhSelectors.userMentionSuggestions(state),
  }
}

function mapDispatchToProps(dispatch, ownProps) {
  const postId = Number(ownProps.postId)
  return {
    ...bindActionCreators(
      {
        fetchPost: apiActions.fetchLearningHubDiscussion,
        fetchComments: apiActions.fetchLearningHubDiscussionComments,
        archivePost: apiActions.archiveLearningHubDiscussion,
        lockPost: apiActions.lockLearningHubDiscussion,
        reopenPost: apiActions.reopenLearningHubDiscussion,
        updateComment: apiActions.updateLearningHubDiscussionComment,
        archiveComment: apiActions.archiveLearningHubDiscussionComment,
        addLike: apiActions.addLearningHubLike,
        destroyLike: apiActions.destroyLearningHubLike,
        fetchUser: apiActions.fetchDirectoryUser,
        flashErrorMessage: flashActions.flashErrorMessage,
        flashSuccessMessage: flashActions.flashSuccessMessage,
        push: routerActions.push,
        redirect: routerActions.replace,
        resetForm: reset,
        createFlag: apiActions.createFlag,
        createLearningHubForumFlag: apiActions.createLearningHubForumFlag,
        unflag: apiActions.unflag,
        disagreeWithFlag: apiActions.disagreeWithFlag,
        fetchMentionSuggestions: apiActions.fetchMentionSuggestions,
        fetchNotifications: apiActions.fetchNotifications,
      },
      dispatch
    ),
    addBookmark: (params) =>
      dispatch(
        params.bookmarkableType === Types.BOOKMARKABLE.USER
          ? apiActions.addBookmark(params)
          : apiActions.addLearningHubBookmark(params)
      ),
    removeBookmark: (params) =>
      dispatch(
        params.bookmarkableType === Types.BOOKMARKABLE.USER
          ? apiActions.removeBookmark(params)
          : apiActions.removeLearningHubBookmark(params)
      ),
    createComment: (commentableId, commentableType, content, attachments) =>
      dispatch(
        apiActions.createLearningHubDiscussionComment(
          postId,
          commentableType === Types.COMMENTABLE.COMMENT
            ? commentableId
            : undefined,
          content,
          attachments
        )
      ),
  }
}

export default compose(
  connectParams('postId'),
  connect(mapStateToProps, mapDispatchToProps)
)(LearningHubDiscussion)
